import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, FileText, FileSpreadsheet, Loader2, CheckCircle, AlertCircle, Cloud, Package, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminImport = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [demoSuppliers, setDemoSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('demo');
  
  // Demo import settings
  const [demoSource, setDemoSource] = useState('dummyjson');
  const [demoLimit, setDemoLimit] = useState(50);
  const [generateAI, setGenerateAI] = useState(false);
  const [catalogStats, setCatalogStats] = useState(null);

  useEffect(() => {
    fetchSuppliers();
    fetchDemoSuppliers();
    fetchCatalogStats();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/admin/suppliers`);
      setSuppliers(response.data.suppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchDemoSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/admin/demo-suppliers`);
      setDemoSuppliers(response.data.suppliers);
    } catch (error) {
      console.error('Error fetching demo suppliers:', error);
    }
  };

  const fetchCatalogStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/catalog/stats`);
      setCatalogStats(response.data);
    } catch (error) {
      console.error('Error fetching catalog stats:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xml'].includes(ext)) {
        toast.error('Formato non supportato. Usa CSV o XML.');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedSupplier) {
      toast.error('Seleziona un fornitore e un file');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/import/bulk?supplier_id=${selectedSupplier}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setResult(response.data);
      fetchCatalogStats();
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} prodotti importati!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
      setResult({ imported: 0, errors: [error.message] });
    } finally {
      setUploading(false);
    }
  };

  const handleDemoImport = async () => {
    setUploading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/import/demo?source=${demoSource}&limit=${demoLimit}&generate_ai=${generateAI}`
      );

      setResult(response.data);
      fetchCatalogStats();
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} prodotti importati da ${response.data.supplier_name}!`);
      } else {
        toast.info('Nessun nuovo prodotto da importare (potrebbero essere già presenti)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
      setResult({ imported: 0, errors: [error.message] });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-import">
      <div>
        <h1 className="text-3xl font-bold font-serif">Importa Prodotti</h1>
        <p className="text-muted-foreground mt-1">Importa prodotti da API demo o file CSV/XML</p>
      </div>

      {/* Catalog Stats */}
      {catalogStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="h-4 w-4" />
              <span className="text-sm">Totale Prodotti</span>
            </div>
            <p className="text-2xl font-bold">{catalogStats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Disponibili</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{catalogStats.available}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span className="text-sm">In Evidenza</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{catalogStats.featured}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Cloud className="h-4 w-4 text-blue-500" />
              <span className="text-sm">Fornitori</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{catalogStats.by_supplier?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Tab Buttons */}
      <div className="flex gap-2 bg-muted/50 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('demo')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'demo' 
              ? 'bg-white shadow text-primary' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-demo"
        >
          <Cloud className="h-4 w-4 inline mr-2" />
          API Demo
        </button>
        <button
          onClick={() => setActiveTab('file')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'file' 
              ? 'bg-white shadow text-primary' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-file"
        >
          <Upload className="h-4 w-4 inline mr-2" />
          File Upload
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Demo Import Tab */}
        {activeTab === 'demo' && (
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-bold font-serif mb-6">Importa da API Demo</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Importa prodotti demo pronti all'uso per testare il marketplace
            </p>

            <div className="space-y-4">
              {/* Source Selection */}
              <div className="space-y-2">
                <Label>Sorgente Prodotti</Label>
                <Select value={demoSource} onValueChange={setDemoSource}>
                  <SelectTrigger data-testid="select-demo-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {demoSuppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex flex-col">
                          <span>{s.name}</span>
                          <span className="text-xs text-muted-foreground">{s.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Limit Selection */}
              <div className="space-y-2">
                <Label>Numero Prodotti</Label>
                <Select value={demoLimit.toString()} onValueChange={(v) => setDemoLimit(parseInt(v))}>
                  <SelectTrigger data-testid="select-demo-limit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 prodotti</SelectItem>
                    <SelectItem value="50">50 prodotti</SelectItem>
                    <SelectItem value="100">100 prodotti</SelectItem>
                    <SelectItem value="194">Tutti (194 max)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* AI Generation Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="font-medium">Genera Contenuti AI</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Titoli e descrizioni in italiano (più lento)
                  </p>
                </div>
                <Switch 
                  checked={generateAI} 
                  onCheckedChange={setGenerateAI}
                  data-testid="toggle-ai"
                />
              </div>

              <Button
                className="w-full"
                onClick={handleDemoImport}
                disabled={uploading}
                data-testid="demo-import-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Importazione in corso...
                  </>
                ) : (
                  <>
                    <Cloud className="h-4 w-4 mr-2" />
                    Importa Prodotti Demo
                  </>
                )}
              </Button>
            </div>

            {/* Result */}
            {result && (
              <div className={`mt-6 p-4 rounded-lg ${
                result.errors?.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'
              }`} data-testid="import-result">
                <div className="flex items-center gap-2 mb-2">
                  {result.imported > 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  )}
                  <span className="font-medium">
                    {result.imported} prodotti importati
                  </span>
                </div>
                {result.supplier_name && (
                  <p className="text-sm text-muted-foreground">
                    Fornitore: {result.supplier_name}
                  </p>
                )}
                {result.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-amber-700">Errori:</p>
                    <ul className="text-sm text-amber-600 list-disc list-inside">
                      {result.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* File Upload Tab */}
        {activeTab === 'file' && (
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-bold font-serif mb-6">Carica File</h2>

            <div className="space-y-4">
              {/* Supplier Selection */}
              <div className="space-y-2">
                <Label>Fornitore *</Label>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger data-testid="select-supplier">
                    <SelectValue placeholder="Seleziona un fornitore" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suppliers.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Nessun fornitore configurato. Aggiungine uno prima.
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label>File (CSV o XML)</Label>
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    file ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                  }`}
                >
                  <input
                    type="file"
                    accept=".csv,.xml"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {file ? (
                      <div className="flex flex-col items-center">
                        {file.name.endsWith('.csv') ? (
                          <FileSpreadsheet className="h-12 w-12 text-primary mb-3" />
                        ) : (
                          <FileText className="h-12 w-12 text-primary mb-3" />
                        )}
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-12 w-12 text-muted-foreground mb-3" />
                        <p className="font-medium">Clicca per caricare</p>
                        <p className="text-sm text-muted-foreground">CSV o XML</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handleUpload}
                disabled={!file || !selectedSupplier || uploading}
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Importazione in corso...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importa Prodotti
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="text-xl font-bold font-serif mb-6">
            {activeTab === 'demo' ? 'API Demo Disponibili' : 'Formato File'}
          </h2>

          {activeTab === 'demo' ? (
            <div className="space-y-4">
              {demoSuppliers.map((s) => (
                <div key={s.id} className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold">{s.name}</h3>
                  <p className="text-sm text-muted-foreground">{s.description}</p>
                  <p className="text-xs text-primary mt-1">
                    {s.product_count} prodotti disponibili
                  </p>
                </div>
              ))}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-700 mb-2">Vantaggi API Demo</h4>
                <ul className="text-sm text-blue-600 space-y-1">
                  <li>• Prodotti pronti all'uso con immagini HD</li>
                  <li>• Nessun file da preparare</li>
                  <li>• Perfetti per testare il marketplace</li>
                  <li>• Aggiornamento automatico prezzi</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* CSV Format */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <FileSpreadsheet className="h-5 w-5" />
                  Formato CSV
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <p>sku,title,description,cost,stock,category,image</p>
                  <p className="text-muted-foreground">SKU001,Prodotto,Descrizione...,19.99,100,Elettronica,https://...</p>
                </div>
              </div>

              {/* XML Format */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5" />
                  Formato XML
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre className="text-xs">{`<products>
  <product>
    <sku>SKU001</sku>
    <title>Prodotto</title>
    <description>Descrizione...</description>
    <cost>19.99</cost>
    <category>Elettronica</category>
    <image>https://...</image>
  </product>
</products>`}</pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminImport;
